/**
 * /admin/activity — "Requests & history": the access questions an admin (or
 * grantor) actually asks day to day, in one place. Pending access requests
 * with inline review on top — a reviewer who misses the bell still sees the
 * queue — and the grant ledger underneath. The raw grant tooling stays on
 * Access (advanced).
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Inbox } from 'lucide-react';
import { useState } from 'react';
import { ReviewAccessRequestDialog } from '../../../components/notifications/ReviewAccessRequestDialog';
import { GrantHistoryPanel } from '../Access/GrantHistoryPanel';
import { useAccessRequests } from '../Access/hooks';
import { ResolvedId } from '../nameResolution';
function formatTimestamp(ts) {
    if (!ts || Number(ts.seconds) === 0) return '—';
    return new Date(Number(ts.seconds) * 1000).toLocaleString();
}
export function ActivityPage() {
    const { data } = useAccessRequests('pending');
    const [reviewId, setReviewId] = useState('');
    const pending = data?.requests ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        "data-testid": Testids.adminActivity.page,
        children: [
            /*#__PURE__*/ _jsxs(Card, {
                "data-testid": Testids.adminActivity.pendingRequestsCard,
                children: [
                    /*#__PURE__*/ _jsxs(CardHeader, {
                        children: [
                            /*#__PURE__*/ _jsxs(CardTitle, {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Inbox, {
                                        className: "h-4 w-4"
                                    }),
                                    "Access requests",
                                    pending.length > 0 && /*#__PURE__*/ _jsxs(Badge, {
                                        variant: "destructive",
                                        children: [
                                            pending.length,
                                            " pending"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(CardDescription, {
                                children: "People asking for access they don’t have. Approving grants the role you pick, exactly where they asked."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CardContent, {
                        children: pending.length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Nothing waiting — new requests show up here and on your notification bell."
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "space-y-2",
                            children: pending.map((r)=>/*#__PURE__*/ _jsxs("div", {
                                    "data-testid": Testids.adminActivity.pendingRequestRow,
                                    "data-request-id": r.id,
                                    className: "flex items-center justify-between gap-3 rounded-md border px-3 py-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "min-w-0 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("p", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: r.requesterUserId,
                                                            name: r.requesterDisplayName
                                                        }),
                                                        " wants to",
                                                        ' ',
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: r.intent
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "truncate text-xs text-muted-foreground",
                                                    children: [
                                                        r.targetObjectType,
                                                        " ·",
                                                        ' ',
                                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: r.targetObjectId,
                                                            name: r.targetDisplayName
                                                        }),
                                                        " ·",
                                                        ' ',
                                                        formatTimestamp(r.createdAt)
                                                    ]
                                                }),
                                                r.message && /*#__PURE__*/ _jsxs("p", {
                                                    className: "truncate text-xs italic",
                                                    children: [
                                                        "“",
                                                        r.message,
                                                        "”"
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            "data-testid": Testids.adminActivity.pendingRequestReview,
                                            onClick: ()=>setReviewId(r.id),
                                            children: "Review"
                                        })
                                    ]
                                }, r.id))
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(GrantHistoryPanel, {}),
            reviewId && /*#__PURE__*/ _jsx(ReviewAccessRequestDialog, {
                requestId: reviewId,
                open: !!reviewId,
                onOpenChange: (o)=>!o && setReviewId('')
            })
        ]
    });
}
